import { Button, IconButton, makeStyles, TableCell, Theme } from "@material-ui/core";
import MuiTableRow from "@material-ui/core/TableRow";
import { COLORS } from "constants/colors";
import { OrderStatusList } from "constants/status";
import useText from "constants/text";
import { checkIsDefined } from "domains/UserAddress/utils";
import { ParcelStatus } from "generated/graphql";
import { EditIcon, RemoveIcon } from "icons";
import React from "react";
import "styled-components/macro";
import StyledCheckbox from "ui/Checkbox";
import MenuActions, { Action } from "./MenuActions";
import { useTableStyles } from "./styles";
import { Column } from "./Table";


export interface FieldGetters {
  [name: string]: (row: any, name: string) => any;
}
export interface RowProps<RowData, RowDataKey extends keyof RowData> {
  columns: Column<RowData>[];
  rowContent: RowData;
  rowsIdentifier: RowDataKey;
  onRowClick?(data: RowData): void;
  onRowDelete?(data: RowData): void;
  onRowEdit?(data: RowData): void;
  onOrderEdit?(data: RowData): void;
  onRowChecked(event: React.MouseEvent<unknown, MouseEvent>, id: string): void;
  onRowCheckedRow(event: React.MouseEvent<unknown, MouseEvent>, id: string): void;
  rowsCount?: number;
  selectRowsCount?: number;
  hasSelection?: boolean;
  rowScanned?: string[];
  isRowSelected(rowIdentifier: any): boolean;
  autoChecked?: boolean;
  isDisabledChecked?: boolean;
  isOrderMenu?: boolean;
  getters?: FieldGetters;
  multipleActions?: Action[];
  onRowMenuClick?(key: string, row: RowData): void;
  rowColor?: string;
  notShowAction?: (row: RowData) => boolean;
}

function getFieldValue(row: any, name: string, getters: FieldGetters) {
  if (getters && getters[name]) {
    return getters[name](row, name);
  }
  return row[name];
}

function FormattedTableCell<T>({ column, row, getters }: { column: Column<T>; row: any; getters: FieldGetters }) {
  if (!column.render && !checkIsDefined(getFieldValue(row, column.field, getters))) return <>N/A</>;
  return <>{column.render ? column.render(row) : row[column.field]}</>;
}

export const TableRow = <RowData, RowDataKey extends keyof RowData>({
  rowContent,
  columns,
  rowsCount,
  selectRowsCount,
  rowsIdentifier,
  onRowChecked,
  onRowCheckedRow,
  hasSelection,
  rowScanned,
  onRowClick,
  onRowDelete,
  onRowEdit,
  onOrderEdit,
  isRowSelected,
  autoChecked = false,
  isDisabledChecked = false,
  getters,
  isOrderMenu = false,
  multipleActions,
  onRowMenuClick,
  rowColor,
  notShowAction,
}: RowProps<RowData, RowDataKey>) => {
  const rowId = (rowContent[rowsIdentifier] as unknown) as string;
  const isSelected = isRowSelected(rowId);
  const [isScanned, setIsScanned] = React.useState(false);
  const { text } = useText();

  const classes = useTableStyles({ color: rowColor });

  React.useEffect(() => {
    if (
      isSelected ||
      rowScanned?.includes(rowId) ||
      // eslint-disable-next-line dot-notation
      rowContent["parcel_status"] === ParcelStatus.Delivered ||
      // eslint-disable-next-line dot-notation
      rowContent["parcel_status"] === ParcelStatus.SentBack
    ) {
      setIsScanned(true);
    }
  }, [isSelected]);

  return (
    <>
      <MuiTableRow
        role="row"
        selected={autoChecked}
        style={{ opacity: rowScanned && !isScanned ? 0.6 : 1 }}
        onClick={(event) => {
          event.stopPropagation();
          onRowClick && onRowClick(rowContent);
        }}
        className={classes.tableCels}
      >
        {hasSelection && (
          <TableCell align="right" padding="checkbox">
            <StyledCheckbox
              color="primary"
              disabled={rowScanned ? !isScanned : isDisabledChecked}
              checked={isSelected || rowsCount === selectRowsCount || autoChecked}
              onClick={(event) => {
                onRowChecked && onRowChecked(event, rowId);
                onRowCheckedRow && onRowCheckedRow(event, rowId);
                event.stopPropagation();
              }}
              css={`
                &.Mui-checked {
                  color: ${COLORS.primary};
                }
              `}
            />
          </TableCell>
        )}
        {columns?.map((column) => (
          <TableCell key={column.field} align="left">
            <FormattedTableCell column={column} row={rowContent} getters={getters} />
          </TableCell>
        ))}
        {multipleActions &&
          // eslint-disable-next-line dot-notation
          (isOrderMenu && !OrderStatusList.all.includes(rowContent["order_status"]) ? null : (
            <TableCell align="left">
              {(!notShowAction || !notShowAction(rowContent)) && (
                <MenuActions actions={multipleActions} row={rowContent} onRowMenuClick={onRowMenuClick} />
              )}
            </TableCell>
          ))}
        {(onRowDelete || onRowEdit || onOrderEdit) && (
          <TableCell align="right">
            {onRowEdit && (
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  onRowEdit(rowContent);
                }}
              >
                <EditIcon width={20} />
              </IconButton>
            )}

            {onRowDelete && (
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  onRowDelete(rowContent);
                }}
              >
                <RemoveIcon width={20} height={20} />
              </IconButton>
            )}
            {onOrderEdit && (
              <Button
                variant="contained"
                className={classes.buttonMenu}
                onClick={(event) => {
                  event.stopPropagation();
                  onOrderEdit(rowContent);
                }}
              >
                {text.all.edit_size}
              </Button>
            )}
          </TableCell>
        )}
      </MuiTableRow>
    </>
  );
};
